import { cn } from "@babackapp/customer-ui/lib/utils";
import { StoreIcon } from "lucide-react";
import { motion, Variants } from "motion/react";
import { useState } from "react";

const headerVariants: Variants = {
  hidden: {
    transition: {
      staggerChildren: 0.025,
      when: "afterChildren",
      duration: 0.2,
    },
  },
  visible: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 5,
    transition: { duration: 0.2, ease: [0.7, 0, 0.84, 0] },
  }, // expo in
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1], // expo out
    },
  },
};

export function Header({
  className,
  ...props
}: React.ComponentProps<typeof motion.div>) {
  return (
    <motion.div
      variants={headerVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      className={cn(
        // [text-align:var(...)] is a Tailwind v4 arbitrary property: one token
        // drives text-align, align-items and justify-content at once, since all
        // three accept the same start|center|end keywords. Below the compact
        // threshold, the logo sits beside the title instead of above it — a row
        // whose cross-axis (vertical) always centers, regardless of the shop's
        // chosen --widget-align, which only makes sense for the column layout.
        "gap-3 flex flex-col compact:flex-row compact:gap-2 compact:items-center not-compact:[align-items:var(--widget-align)] [text-align:var(--widget-align)] [justify-content:var(--widget-align)]",
        className,
      )}
      {...props}
    />
  );
}

export function HeaderTitle({
  className,
  ...props
}: React.ComponentProps<typeof motion.h3>) {
  return (
    <motion.h3
      variants={itemVariants}
      className={cn(
        "text-xl compact:text-base font-sans inline-flex gap-1 items-center",
        className,
      )}
      {...props}
    />
  );
}

// Shop logos come in any aspect ratio, so the box is bounded on both axes and
// the image fits inside it: a landscape logo grows until it hits the width, a
// portrait one until it hits the height. Tune the whole widget from here.
const SHOP_LOGO_BOX = "max-h-14 max-w-[190px] compact:max-h-6 compact:max-w-24";
// Fallback mark, sized to sit at roughly the same visual weight.
const SHOP_LOGO_FALLBACK_SIZE = "size-10 compact:size-5";

export function ShopLogo({
  className,
  src,
  ...props
}: React.ComponentProps<typeof motion.img>) {
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <motion.div
        layoutId="shop-logo"
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        className={cn("my-2", className)}
      >
        <StoreIcon
          className={cn(SHOP_LOGO_FALLBACK_SIZE, "text-muted-foreground")}
        />
      </motion.div>
    );
  }

  return (
    <motion.img
      layoutId="shop-logo"
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      className={cn(
        "my-2 h-auto w-auto object-contain",
        SHOP_LOGO_BOX,
        className,
      )}
      src={src}
      onError={() => setError(true)}
      {...props}
    />
  );
}

export function HeaderDescription({
  className,
  ...props
}: React.ComponentProps<typeof motion.p>) {
  return (
    <motion.p
      variants={itemVariants}
      className={cn(
        // Cut first when space is tight: it's the least load-bearing line in
        // the header, and every page using <Header> gets this for free.
        "text-muted-foreground/80 text-sm max-w-[400px] font-content font-normal text-balance compact:hidden",
        className,
      )}
      {...props}
    />
  );
}

export function SectionHeader({
  className,
  ...props
}: React.ComponentProps<typeof motion.span>) {
  return (
    <motion.span
      initial={{ x: -20, opacity: 0 }}
      animate={{
        x: 0,
        opacity: 1,
        transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
      }}
      exit={{
        x: -20,
        opacity: 0,
        transition: { duration: 0.2, ease: [0.7, 0, 0.84, 0] },
      }}
      className={cn("font-medium text-xs text-muted-foreground", className)}
      {...props}
    />
  );
}
